import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import PageLoadingSpinner from '../components/ehr/PageLoadingSpinner';
import ENV from '../constants/Env';
import EhrErrorMessage from '../components/ehr/EhrErrorMessage';
import DebugInformation from '../components/ehr/DebugInfo';
import { captureLog } from '../lib/error-handler';
import { useAuth } from '../contexts/auth-context';
import {
  readEhrSession,
  saveEhrSession,
  TokenResponse,
  useEhrContext,
} from '../contexts/ehr-context';

const { apiUrl } = ENV;

type PageState =
  | 'auth'
  | 'loading'
  | 'fhirError'
  | 'invalidApp'
  | 'missingContextFields'
  | 'missingEhrSession'
  | 'missingEmail'
  | 'networkError'
  | 'success';

/**
 * From the `Launcher` page, the EHR will redirect to this page once we are
 * authorized with the EHR session. At that point, we will make a request to get
 * the custodian for the EHR user.
 */
export default function LaunchSession() {
  const { ehrSession, setEhrSession, ehrToken, fhirError } = useEhrContext();
  const history = useHistory();
  const auth = useAuth();
  const apolloClient = useApolloClient();
  const [state, setState] = useState<PageState>('loading');
  const [missingFields, setMissingFields] = useState<string[]>([]);

  const missingRequiredFields = (tokenResponse: TokenResponse) => {
    if (!tokenResponse.userEmail) {
      setState('missingEmail');
      captureLog('error', '[Epic EHR] - Launch Session - Missing email');
      return true;
    }

    if (tokenResponse.patient === undefined || tokenResponse.patient === '') {
      // Not in a patient context, so we don't need to check for patient fields.
      return false;
    }

    const requiredFields = [
      'patientFirstName',
      'patientLastName',
      'patientDOB',
    ];
    const missing: string[] = [];

    requiredFields.forEach(field => {
      if (!Object.keys(tokenResponse || {}).includes(field)) {
        missing.push(field);
      }
    });

    if (missing.length > 0) {
      setState('missingContextFields');
      setMissingFields(missing);
      captureLog(
        'error',
        `[Epic EHR] - Launch Session - Missing patient context fields: ${missing.join(
          ', ',
        )}`,
      );
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (!fhirError) {
      return;
    }

    captureLog('error', `[Epic EHR] - Launch Session - FHIR client error: ${fhirError}`);
    setState('fhirError');
  }, [fhirError]);

  useEffect(() => {
    // We need to read this from session storage and then set it in the context
    // now that the EHR has finished the redirect.
    const session = readEhrSession();

    if (!session) {
      setState('missingEhrSession');
      captureLog('error', '[Epic EHR] - Launch Session - Missing EHR Session in session storage');
      return;
    }

    setEhrSession(session);
  }, [setEhrSession]);

  useEffect(() => {
    if (!ehrToken || !ehrSession) {
      return;
    }

    ehrSession.ehrAccessToken = ehrToken;
    // TODO: Should we be spreading over the session here to cause a re-render?
    //       Example: setEhrSession({ ...ehrSession });
    saveEhrSession(ehrSession);
    setEhrSession(ehrSession);

    if (missingRequiredFields(ehrToken)) {
      return;
    }

    if (auth.isLoggedIn) {
      // Force a logout in case this is a different EHR user launching the app.
      auth.logout(apolloClient); 
    }

    const bodyData = { ...ehrSession };
    const headers: HeadersInit = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    const createSession = async () => {
      let response: Response;

      try {
        response = await fetch(`${apiUrl}/api/ehr/sessions`, {
          method: 'POST',
          headers,
          body: JSON.stringify(bodyData),
        });
      } catch (error) {
        captureLog('error', `[Epic EHR] - Launch Session - Network error when trying to create session: ${error}`);
        setState('networkError');
        return;
      }

      if (response.ok) {
        setState('success');
        const accessToken = response.headers.get('access-token');
        const client = response.headers.get('client');
        const expiry = response.headers.get('expiry');
        const uid = response.headers.get('uid');
        const { data } = await response.json();
        const { custodian_id, id } = data;

        if (id) {
          ehrSession.ehrSessionId = id;
          // TODO: Should we be spreading over the session here to cause a re-render?
          //       Example: setEhrSession({ ...ehrSession });
          saveEhrSession(ehrSession);
          setEhrSession(ehrSession);
        }

        // TODO: This is temporary while investigating an issue. We probably
        // don't need this long term.
        if (
          custodian_id === undefined ||
          custodian_id === null ||
          custodian_id === '' ||
          custodian_id === 0
        ) {
          let logId = custodian_id;
          if (custodian_id === undefined) {
            logId = '<undefined>';
          } else if (custodian_id === null) {
            logId = '<null>';
          } else if (custodian_id === '') {
            logId = '<empty string>';
          }

          captureLog('error', `[Epic EHR] - Launch Session - Invalid custodian ID '${logId}' returned when creating the EHR session`);
        }

        if (accessToken && client && expiry && custodian_id && uid) {
          const token = { accessToken, client, expiry, id: custodian_id, uid };
          auth.storeAuthHeaders(token);
        }

        if (ehrToken.patient === undefined || ehrToken.patient === '') {
          history.push('/');
        } else {
          history.push('/launch/fhir');
        }
      } else {
        if (response.status === 422) {
          captureLog('error', `[Epic EHR] - Launch Session - Error with FHIR App and Client ID combo (App ID: '${ehrSession.fhirAppId}', Client ID: '${ehrSession.fhirAppClientId}')`);
          setState('invalidApp');
        } else {
          captureLog('error', `[Epic EHR] - Launch Session - Error authenticating user '${ehrToken?.userEmail}' with Onward`);
          setState('auth');
        }
      }
    };

    createSession();
    // We don't want to include `auth` in the dependency check here because after
    // we call the API to create the token, we call the `auth.storeAuthHeaders`
    // function, which actually causes `auth` to change and trigger this `useEffect`
    // again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ehrSession, ehrToken, history, setEhrSession]);

  return (
    <>
      {(state === 'loading' || state === 'success') && <PageLoadingSpinner />}

      {state === 'missingEmail' && (
        <EhrErrorMessage>
          <EhrErrorMessage.Title>Email address missing</EhrErrorMessage.Title>
          <EhrErrorMessage.Body>
            <p>
              Onward requires an email address for the logged in user and it is
              missing from the launch context.
            </p>

            <DebugInformation tokenResponse={ehrToken} />
          </EhrErrorMessage.Body>
        </EhrErrorMessage>
      )}

      {state === 'missingContextFields' && (
        <EhrErrorMessage>
          <EhrErrorMessage.Title>
            Launch context is missing fields
          </EhrErrorMessage.Title>
          <EhrErrorMessage.Body>
            <p>
              You are launching Onward in the context of a patient, but there
              are missing fields in the launch context.
            </p>

            {ehrSession?.testSession && (
              <>
                <p>The following fields are missing from the launch context:</p>

                <ul>
                  {missingFields.map(field => (
                    <li key={field}>{field}</li>
                  ))}
                </ul>
              </>
            )}

            <DebugInformation tokenResponse={ehrToken} />
          </EhrErrorMessage.Body>
        </EhrErrorMessage>
      )}

      {state === 'auth' && (
        <EhrErrorMessage>
          <EhrErrorMessage.Title>Not authorized</EhrErrorMessage.Title>
          <EhrErrorMessage.Body>
            <p>
              We were unable to authorize you with Onward. Make sure that you
              have an Onward account using this email address:{' '}
              {ehrToken?.userEmail}
            </p>

            <DebugInformation tokenResponse={ehrToken} />
          </EhrErrorMessage.Body>
        </EhrErrorMessage>
      )}

      {state === 'fhirError' && (
        <EhrErrorMessage>
          <EhrErrorMessage.Title>EHR Error</EhrErrorMessage.Title>
          <EhrErrorMessage.Body>
            <p>
              We were unable to connect to the EHR. Please try launching Onward
              again from the EHR.
            </p>

            <DebugInformation tokenResponse={ehrToken} />
          </EhrErrorMessage.Body>
        </EhrErrorMessage>
      )}

      {state === 'missingEhrSession' && (
        <EhrErrorMessage>
          <EhrErrorMessage.Title>Missing Session</EhrErrorMessage.Title>
          <EhrErrorMessage.Body>
            <p>
              We were unable to load the EHR session. This is likely due to
              issues with OAuth in the EHR.
            </p>

            <DebugInformation tokenResponse={ehrToken} />
          </EhrErrorMessage.Body>
        </EhrErrorMessage>
      )}

      {state === 'networkError' && (
        <EhrErrorMessage>
          <EhrErrorMessage.Title>Network Error</EhrErrorMessage.Title>
          <EhrErrorMessage.Body>
            <p>
              We were unable to create a session with Onward. Please check your
              network connection and try again.
            </p>

            <DebugInformation tokenResponse={ehrToken} />
          </EhrErrorMessage.Body>
        </EhrErrorMessage>
      )}

      {state === 'invalidApp' && (
        <EhrErrorMessage>
          <EhrErrorMessage.Title>Not authorized</EhrErrorMessage.Title>
          <EhrErrorMessage.Body>
            <p>
              We were unable to authorize you with Onward. The FHIR App ID and
              Client ID combination is not valid.
            </p>

            <DebugInformation tokenResponse={ehrToken} />
          </EhrErrorMessage.Body>
        </EhrErrorMessage>
      )}
    </>
  );
}
